'use client';

import { useTranslation } from 'react-i18next';
import {
  Stack,
  Group,
  Text,
  Code,
  UnstyledButton,
  Textarea,
  Box,
} from '@mantine/core';
import { IconArrowRight, IconSparkles } from '@tabler/icons-react';

import Checkbox from '@ui-kit/atoms/Checkbox';
import { QUICK_PROMPTS } from './quickPrompts';
import * as classes from './styles/index.css';

interface AutomaticViewProps {
  selectedPromptKey: string | null;
  customPrompt: string;
  onSelectPrompt: (key: string | null, prompt: string) => void;
  onCustomPromptChange: (value: string) => void;
}

const CUSTOM_PROMPT_KEY = 'customPrompt';

export default function AutomaticView({
  selectedPromptKey,
  customPrompt,
  onSelectPrompt,
  onCustomPromptChange,
}: AutomaticViewProps) {
  const { t } = useTranslation();

  function handleCardClick(key: string) {
    if (key === CUSTOM_PROMPT_KEY) {
      if (selectedPromptKey === CUSTOM_PROMPT_KEY) {
        onSelectPrompt(null, '');
      } else {
        onSelectPrompt(CUSTOM_PROMPT_KEY, customPrompt);
      }
      return;
    }

    const qp = QUICK_PROMPTS.find((p) => p.key === key);
    if (!qp) return;

    if (selectedPromptKey === key) {
      onSelectPrompt(null, '');
    } else {
      onSelectPrompt(key, qp.prompt);
    }
  }

  return (
    <Stack gap="xs">
      {QUICK_PROMPTS.map((qp) => {
        const isSelected = selectedPromptKey === qp.key;

        return (
          <UnstyledButton
            key={qp.key}
            onClick={() => handleCardClick(qp.key)}
            px="sm"
            py="xs"
            className={`${classes.promptCard} ${isSelected ? classes.promptCardSelected : ''}`}
          >
            <Group gap="sm" wrap="nowrap" align="center">
              <Checkbox
                checked={isSelected}
                onChange={() => handleCardClick(qp.key)}
                tabIndex={-1}
                styles={{ input: { cursor: 'pointer' } }}
              />
              <Text size="sm" fw={500}>
                {t(qp.labelKey)}
              </Text>
              <Group gap={6} align="center" ml="auto" wrap="nowrap">
                <Code
                  fz="xs"
                  c="var(--mantine-color-gray-7)"
                  bg="var(--mantine-color-gray-1)"
                >
                  {qp.exampleBefore}
                </Code>
                <IconArrowRight size={12} color="var(--mantine-color-gray-6)" />
                <Code fz="xs" color="blackroomBlue" c="white">
                  {qp.exampleAfter}
                </Code>
              </Group>
            </Group>
          </UnstyledButton>
        );
      })}

      <UnstyledButton
        onClick={() => handleCardClick(CUSTOM_PROMPT_KEY)}
        px="sm"
        py="xs"
        className={`${classes.promptCard} ${selectedPromptKey === CUSTOM_PROMPT_KEY ? classes.promptCardSelected : ''}`}
      >
        <Group gap="sm" wrap="nowrap" align="center">
          <Checkbox
            checked={selectedPromptKey === CUSTOM_PROMPT_KEY}
            onChange={() => handleCardClick(CUSTOM_PROMPT_KEY)}
            tabIndex={-1}
            styles={{ input: { cursor: 'pointer' } }}
          />
          <Group gap="0.6rem">
            <IconSparkles size={16} />
            <Text size="sm" fw={500}>
              {t('Resource:DialogResourceEdit.suggestWithAI')}
            </Text>
          </Group>
          <Text size="xs" c="dimmed" ml="auto">
            {t('Resource:DialogResourceEdit.customPromptHint')}
          </Text>
        </Group>
      </UnstyledButton>

      {selectedPromptKey === CUSTOM_PROMPT_KEY && (
        <Box pl={36}>
          <Textarea
            placeholder={t(
              'Resource:DialogResourceEdit.customPromptPlaceholder',
            )}
            value={customPrompt}
            onChange={(event) =>
              onCustomPromptChange(event.currentTarget.value)
            }
            minRows={3}
            maxRows={6}
            autosize
            autoFocus
            onClick={(e) => e.stopPropagation()}
          />
        </Box>
      )}
    </Stack>
  );
}
